import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';

import { BasicInfoPanel } from './BasicInfoPanel';
import { LocationsPanel } from './LocationsPanel';
import { ProfessionalPanel } from './ProfessionalPanel';
import { BiographyPanel } from './BiographyPanel';
import type { Person } from '../../../../types';

interface DetailsAccordionProps {
  person: Person;
  showMetadata?: boolean;
  metadataFormat?: 'short' | 'long';
}

type SectionKey = 'basic' | 'locations' | 'professional' | 'biography';

/**
 * DetailsAccordion - Collapsible details sections with deep linking
 *
 * Features:
 * - URL hash-based expansion (#details-basic, #details-locations, etc.)
 * - Controlled accordion expansion
 * - Themed panels for different information types
 */
export const DetailsAccordion: React.FC<DetailsAccordionProps> = ({
  person,
  showMetadata = false,
  metadataFormat = 'short'
}) => {
  const location = useLocation();
  const [expanded, setExpanded] = useState<SectionKey | false>('basic');

  // Deep linking: Expand section based on URL hash
  useEffect(() => {
    if (location.hash) {
      const hashSection = location.hash.replace('#details-', '') as SectionKey;
      const validSections: SectionKey[] = ['basic', 'locations', 'professional', 'biography'];

      if (validSections.includes(hashSection)) {
        setExpanded(hashSection);

        // Scroll to the accordion after a short delay to allow render
        setTimeout(() => {
          const element = document.getElementById(`details-${hashSection}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    }
  }, [location.hash]);

  const handleChange = (panel: SectionKey) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box>
      {/* Basic Information */}
      <Accordion
        id="details-basic"
        expanded={expanded === 'basic'}
        onChange={handleChange('basic')}
        sx={{ mb: 1 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="details-basic-content"
          id="details-basic-header"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon color="primary" />
            <Typography fontWeight={600}>Basic Information</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <BasicInfoPanel
            person={person}
            showMetadata={showMetadata}
            metadataFormat={metadataFormat}
          />
        </AccordionDetails>
      </Accordion>

      {/* Locations */}
      <Accordion
        id="details-locations"
        expanded={expanded === 'locations'}
        onChange={handleChange('locations')}
        sx={{ mb: 1 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="details-locations-content"
          id="details-locations-header"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationIcon color="primary" />
            <Typography fontWeight={600}>Locations</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <LocationsPanel
            person={person}
            showMetadata={showMetadata}
            metadataFormat={metadataFormat}
          />
        </AccordionDetails>
      </Accordion>

      {/* Professional Information */}
      <Accordion
        id="details-professional"
        expanded={expanded === 'professional'}
        onChange={handleChange('professional')}
        sx={{ mb: 1 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="details-professional-content"
          id="details-professional-header"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WorkIcon color="primary" />
            <Typography fontWeight={600}>Professional Information</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <ProfessionalPanel
            person={person}
            showMetadata={showMetadata}
            metadataFormat={metadataFormat}
          />
        </AccordionDetails>
      </Accordion>

      {/* Biography */}
      <Accordion
        id="details-biography"
        expanded={expanded === 'biography'}
        onChange={handleChange('biography')}
        sx={{ mb: 1 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="details-biography-content"
          id="details-biography-header"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescriptionIcon color="primary" />
            <Typography fontWeight={600}>Biography</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <BiographyPanel
            person={person}
            showMetadata={showMetadata}
            metadataFormat={metadataFormat}
          />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
